import React, { useState, useEffect } from 'react';
import TrackDetail from './TrackDetail'; // Import the new component

function App() {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [checkedTracks, setCheckedTracks] = useState({});
  const [selectedTracksDetails, setSelectedTracksDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [packaging, setPackaging] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [qualityLevel, setQualityLevel] = useState('jymaster');
  const [theme, setTheme] = useState(() => {
    if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    return 'light';
  });

  // Effect to apply the theme to the root <html> element
  useEffect(() => {
    document.documentElement.setAttribute('data-bs-theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => (prev === 'light' ? 'dark' : 'light'));
  };

  const API_URL = `${process.env.REACT_APP_API_URL}/api/music`;

  const handleSearch = async () => {
    setLoading(true);
    setError(null);
    setSearchResults([]);
    setCheckedTracks({});
    setSelectedTracksDetails([]);
    setSubmitted(false);

    try {
      const response = await fetch(`${API_URL}/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ keywords: searchTerm }),
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setSearchResults(data.data.songs || []);
    } catch (err) {
      console.error('Search error:', err);
      setError('搜尋音樂時發生錯誤。請稍後再試。');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setSearchTerm('');
    setSearchResults([]);
    setCheckedTracks({});
    setSelectedTracksDetails([]);
    setError(null);
    setSubmitted(false);
  };

  const handleCheckboxChange = (trackId) => {
    setCheckedTracks(prev => ({ ...prev, [trackId]: !prev[trackId] }));
  };

  const handleSubmit = async () => {
    const checkedIds = Object.keys(checkedTracks).filter(id => checkedTracks[id]);
    if (checkedIds.length === 0) {
      alert('請至少選擇一首歌曲！');
      return;
    }

    setLoading(true);
    setError(null);
    setSelectedTracksDetails([]);

    try {
      const trackDetailsPromises = checkedIds.map(async (id) => {
        const [detailResponse, lyricResponse, urlResponse] = await Promise.all([
          fetch(`${API_URL}/detail`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id }) }),
          fetch(`${API_URL}/lyric`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id }) }),
          fetch(`${API_URL}/url`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id, level: qualityLevel }) }),
        ]);

        if (!detailResponse.ok || !lyricResponse.ok || !urlResponse.ok) {
          console.error(`Error fetching details for track ${id}`);
          return null;
        }

        const detailData = await detailResponse.json();
        const lyricData = await lyricResponse.json();
        const urlData = await urlResponse.json();

        if (!detailData.data || !detailData.data.id) {
          console.error(`Unexpected detail response structure for track ${id}:`, detailData);
          return null;
        }

        const trackInfo = {
            id: detailData.data.id,
            name: detailData.data.name,
            ar: detailData.data.singer ? detailData.data.singer.split('/').map(name => ({ name })) : [],
            al: { name: detailData.data.album, picUrl: detailData.data.picimg },
            lyrics: lyricData.data && lyricData.data.lrc ? lyricData.data.lrc : '無歌詞',
            playUrl: urlData.data && urlData.data[0] ? urlData.data[0].url : null,
        };
        return trackInfo;
      });

      const results = await Promise.all(trackDetailsPromises);
      setSelectedTracksDetails(results.filter(Boolean));
      setSubmitted(true);
    } catch (err) {
      console.error('Submit error:', err);
      setError('提交選擇時發生錯誤。');
    } finally {
      setLoading(false);
    }
  };

  const handlePackageDownload = async () => {
    if (selectedTracksDetails.length === 0) {
      alert('沒有可打包的歌曲。請先送出選擇。');
      return;
    }
    setPackaging(true);
    setError(null);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/download/packaged`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tracks: selectedTracksDetails }),
      });
      if (!response.ok) throw new Error(`Server error: ${response.statusText}`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = 'music_package.zip';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Packaging error:', err);
      setError('打包下載時發生錯誤。');
    } finally {
      setPackaging(false);
    }
  };

  return (
    <div className={`App bg-body text-body`}>
      <div className="container py-5">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 className="mb-0">音樂解析器</h1>
          <button className="btn btn-outline-secondary" onClick={toggleTheme}>
            {theme === 'light' ? '切換到暗色模式' : '切換到亮色模式'}
          </button>
        </div>

        <div className="card p-4 shadow-sm mb-4">
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              placeholder="輸入歌曲名稱或藝人進行搜尋..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => { if (e.key === 'Enter') handleSearch(); }}
            />
            <button className="btn btn-primary" onClick={handleSearch} disabled={loading}>
              {loading && searchResults.length === 0 ? '搜尋中...' : '搜尋'}
            </button>
            <button className="btn btn-outline-secondary" onClick={handleClear}>清空</button>
          </div>
          <div className="mt-3">
            <label htmlFor="quality-select" className="form-label">選擇音質</label>
            <select
              id="quality-select"
              className="form-select"
              value={qualityLevel}
              onChange={(e) => setQualityLevel(e.target.value)}
            >
              <option value="standard">標準音質</option>
              <option value="higher">較高音質</option>
              <option value="exhigh">極高音質</option>
              <option value="lossless">無損音質</option>
              <option value="hires">Hi-Res音質</option>
              <option value="jyeffect">高清環繞聲</option>
              <option value="sky">沉浸環繞聲</option>
              <option value="jymaster">超清母帶</option>
            </select>
          </div>
          {error && <div className="alert alert-danger mt-3">{error}</div>}
        </div>

        {!submitted && searchResults.length > 0 && (
          <div className="card p-4 shadow-sm mb-4">
            <h2 className="mb-3">搜尋結果</h2>
            <div className="list-group">
              {searchResults.map((track) => (
                <label key={track.id} className="list-group-item">
                  <input
                    className="form-check-input me-2"
                    type="checkbox"
                    checked={!!checkedTracks[track.id]}
                    onChange={() => handleCheckboxChange(track.id)}
                  />
                  {track.name} - {track.artists} ({track.album})
                </label>
              ))}
            </div>
            <button className="btn btn-success mt-3" onClick={handleSubmit} disabled={loading}>
              {loading ? '處理中...' : '送出'}
            </button>
          </div>
        )}
        
        {selectedTracksDetails.length > 0 && (
          <div className="mt-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h2 className="mb-0">已選歌曲詳情</h2>
              <div>
                <button className="btn btn-primary" onClick={handlePackageDownload} disabled={packaging}>
                  {packaging ? '打包中...' : '下載打包 (ZIP)'}
                </button>
                {packaging && <span className="ms-2 text-muted">正在為您打包 {selectedTracksDetails.length} 首歌曲，這可能需要一些時間...</span>}
              </div>
            </div>
            {selectedTracksDetails.map((track, index) => (
              <TrackDetail key={track.id || index} track={track} />
            ))}
          </div>
        )}
      </div>

      <footer className={`footer mt-auto py-3 bg-${theme === 'light' ? 'light' : 'dark'} text-center`}>
        <div className="container">
          <span className="text-muted">
            感謝 <a href="https://github.com/Suxiaoqinx/Netease_url" target="_blank" rel="noopener noreferrer">Netease_url 專案</a> 提供的 API 參考實作。
          </span>
        </div>
      </footer>
    </div>
  );
}

export default App;
